import { useState } from 'react'

import { F0Box, F0Button } from '@factorialco/f0-react'
import {
  AtsHiringPlanApproveRequisitionDocument,
  AtsHiringPlanCancelRequisitionDocument,
  AtsHiringPlanRequisitionDetailDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DeleteRequisitionModal } from 'modules/ats/components/DeleteRequisitionModal'

import { RejectRequisitionDialog } from './RejectRequisitionDialog'

interface Props {
  requisitionId: string
  authorId: string
  currentApproverIds?: string[]
  canManageJobPostings: boolean
  isPending: boolean
  onDeleteSuccess?: () => void
}

export const RequestActions = ({
  requisitionId,
  authorId,
  currentApproverIds,
  canManageJobPostings,
  isPending,
  onDeleteSuccess,
}: Props) => {
  const {
    access: { id: accessId },
  } = useCurrentSession()
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const isAuthor = authorId === accessId
  const canDelete = isAuthor || canManageJobPostings
  const isApprover = currentApproverIds?.includes(accessId)

  const [cancelMutation, { loading: canceling }] = useMutation(
    AtsHiringPlanCancelRequisitionDocument,
    {
      extractorFn: (data) => data.atsMutations.cancelAtsRequisition,
      refetchQueries: [AtsHiringPlanRequisitionDetailDocument],
    }
  )

  const [approveMutation, { loading: approving }] = useMutation(
    AtsHiringPlanApproveRequisitionDocument,
    {
      extractorFn: (data) => data.atsMutations.approveAtsRequisition,
      refetchQueries: [AtsHiringPlanRequisitionDetailDocument],
    }
  )

  const onCancel = async () => {
    const result = await cancelMutation({
      variables: { id: requisitionId },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.vacancies_requisition_page.requisition_detail.actions.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.vacancies_requisition_page.requisition_detail.actions.cancelled'),
      })
    }
  }

  const onApprove = async () => {
    const result = await approveMutation({
      variables: { id: requisitionId, approverId: accessId },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.vacancies_requisition_page.requisition_detail.actions.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.vacancies_requisition_page.requisition_detail.actions.approved'),
      })
    }
  }

  return (
    <>
      {showDeleteModal && (
        <DeleteRequisitionModal
          id={requisitionId}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={onDeleteSuccess}
        />
      )}
      {showRejectDialog && (
        <RejectRequisitionDialog
          requisitionId={requisitionId}
          onClose={() => setShowRejectDialog(false)}
        />
      )}
      <F0Box
        display="flex"
        flexDirection="row"
        justifyContent="between"
        alignItems="center"
        width="full"
      >
        <F0Box>
          {canDelete && (
            <F0Button
              variant="critical"
              label={i18n.t('ats.jobs_requisitions.requisition_detail.delete_request')}
              onClick={() => setShowDeleteModal(true)}
            />
          )}
        </F0Box>
        <F0Box display="flex" gap="sm">
          {isPending && isAuthor && (
            <F0Button
              variant="neutral"
              label={i18n.t('ats.vacancies_requisition_page.requisition_detail.cancel_request')}
              onClick={onCancel}
              loading={canceling}
            />
          )}
          {isPending && !isAuthor && isApprover && (
            <>
              <F0Button
                variant="outline"
                label={i18n.t('ats.vacancies_requisition_page.requisition_detail.reject_request')}
                onClick={() => setShowRejectDialog(true)}
              />
              <F0Button
                label={i18n.t('ats.vacancies_requisition_page.requisition_detail.approve_request')}
                onClick={onApprove}
                loading={approving}
              />
            </>
          )}
        </F0Box>
      </F0Box>
    </>
  )
}
